import React, { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, FileWarningIcon, SendIcon } from 'lucide-react';
import { PortalHeader } from '../components/shell/PortalHeader';
import { JourneyStages } from '../components/shell/JourneyStages';
import { ComplianceFooter } from '../components/shell/ComplianceFooter';
import { LoanOfficerChip } from '../components/shell/LoanOfficerChip';
import { PricingStatusBadge, PricingStatusNote } from '../components/loan/PricingStatusNote';
import { Button } from '../components/ui/Button';
import { usePortal } from '../contexts/PortalContext';
import { loanOptions } from '../data/loanOptions';
import { buildReview } from '../utils/review';
import { formatCurrency } from '../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function SubmitApplication() {
  const navigate = useNavigate();
  const {
    answers,
    selectedOption,
    creditAuthorized,
    submitApplication
  } = usePortal();
  const [certified, setCertified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sections = useMemo(() => buildReview(answers), [answers]);
  const option = loanOptions.find(o => o.id === selectedOption);
  if (!option) return <Navigate to="/options" replace data-id="element-1026" data-mp="2mjxn0tt8gcuj" />;
  const issues = sections.reduce((n, s) => n + s.issues.length, 0);
  const ready = issues === 0 && creditAuthorized && certified;
  const submit = () => {
    setSubmitting(true);
    window.setTimeout(() => {
      submitApplication();
      navigate('/complete');
    }, 1200);
  };
  return <div className="min-h-screen w-full bg-canvas" data-id="element-1027" data-mp="30k8e9xygftg8">
      <PortalHeader maxWidth="max-w-5xl" right={<LoanOfficerChip data-id="element-1029" data-mp="35hs31rss3omf" />} data-id="element-1028" data-mp="35ite3w9sob45" />
      <main className="mx-auto max-w-[640px] px-5 pb-10 pt-8 md:pt-14" data-id="element-1030" data-mp="3t95vryuudm7x">
        <JourneyStages current={2} currentProgress={92} data-id="element-1031" data-mp="3t7umxkmjho9y" />

        <h1 className="mt-10 text-[28px] font-semibold leading-tight tracking-[-0.025em] text-ink-900 md:text-[36px]" data-id="element-1032" data-mp="1ursg45njrj1">Submit your application</h1>
        <p className="mt-3 text-[17px] leading-relaxed text-ink-500" data-id="element-1033" data-mp="boe0c3eot5bv">One last look. Nothing is sent to your loan team as a completed application until you submit.</p>

        <motion.section initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="mt-8 rounded-2xl bg-white ring-1 ring-ink-200" aria-labelledby="sel-title" data-id="element-1034" data-mp="2oh6lndfxg9y">
          <header className="flex items-center justify-between gap-3 px-5 pt-5" data-id="element-1035" data-mp="3oyl5m1t6dpom">
            <h2 id="sel-title" className="font-semibold text-ink-900" data-id="element-1036" data-mp="3k5iahp8r286b">
              Your selected option
            </h2>
            <Link to="/options" className="text-sm font-medium text-pulse-700 hover:underline" data-id="element-1037" data-mp="3f7gqn23i24ts">
              Change
            </Link>
          </header>
          <div className="px-5 pb-5 pt-3" data-id="element-1038" data-mp="36727o9e0d1tg">
            <div className="flex flex-wrap items-center gap-2.5" data-id="element-1039" data-mp="365qzzyhfz9x2">
              <p className="text-lg font-semibold text-ink-900" data-id="element-1040" data-mp="318h2rwnt9tyo">{option.name}</p>
              <PricingStatusBadge data-id="element-1041" data-mp="1gpwv6kwx64te" />
            </div>
            <dl className={`mt-4 grid gap-3 text-sm ${option.cashToClose != null ? 'grid-cols-3' : 'grid-cols-2'}`} data-id="element-1042" data-mp="1gp567sxtv5c3">
              <div data-id="element-1043" data-mp="1lqb4s7e65mtj">
                <dt className="text-ink-500" data-id="element-1044" data-mp="1qnujf8es41m0">Est. payment</dt>
                <dd className="tabular mt-0.5 font-medium text-ink-900" data-id="element-1045" data-mp="1qlgmsv4gahcc">{formatCurrency(option.monthly)}/mo</dd>
              </div>
              <div data-id="element-1046" data-mp="xz5q3vjyt0kt">
                <dt className="text-ink-500" data-id="element-1047" data-mp="xzoxiibzo6kl">Rate</dt>
                <dd className="tabular mt-0.5 font-medium text-ink-900" data-id="element-1048" data-mp="xx2o0d5jfgzq">{option.rate.toFixed(3)}%</dd>
              </div>
              {option.cashToClose != null && <div data-id="element-1049" data-mp="16y0vj5f6q6n2">
                  <dt className="text-ink-500" data-id="element-1050" data-mp="16xhcfldstc7g">Cash to close</dt>
                  <dd className="tabular mt-0.5 font-medium text-ink-900" data-id="element-1051" data-mp="1br3rtlbcju6y">{formatCurrency(option.cashToClose)}</dd>
                </div>}
            </dl>
            <div className="mt-4 rounded-xl bg-ink-50 px-4 py-3" data-id="element-1052" data-mp="j9hmgnob9ptx">
              <PricingStatusNote data-id="element-1053" data-mp="j7wun5tdskzr" />
            </div>
          </div>
        </motion.section>

        <section className="mt-4 rounded-2xl bg-white ring-1 ring-ink-200" aria-labelledby="app-title" data-id="element-1054" data-mp="3a2uuzuqbyw">
          <header className="flex items-center justify-between gap-3 px-5 pt-5" data-id="element-1055" data-mp="3r9l0eh5nkulh">
            <h2 id="app-title" className="font-semibold text-ink-900" data-id="element-1056" data-mp="9wije5zyejqu">
              Your application
            </h2>
            <Link to="/apply/review" className="text-sm font-medium text-pulse-700 hover:underline" data-id="element-1057" data-mp="52moxdiwff0f">
              Review details
            </Link>
          </header>
          <ul className="grid gap-x-6 gap-y-2.5 px-5 pb-5 pt-4 sm:grid-cols-2" data-id="element-1058" data-mp="dvzt5bmey31t">
            {sections.map(s => <li key={s.id} className="flex items-center gap-2 text-[15px]" data-id="element-1059" data-mp="sta9aya03x3v">
                {s.issues.length ? <AlertCircleIcon className="h-4 w-4 text-critical-500" aria-label="Needs attention" data-id="element-1060" data-mp="nvqjd13xxvw3" /> : <CheckCircle2Icon className="h-4 w-4 text-positive-500" aria-label="Complete" data-id="element-1061" data-mp="2ulyg1hlut2oe" />}
                <span className={s.issues.length ? 'text-critical-700' : 'text-ink-700'} data-id="element-1062" data-mp="2pnwmu3uprmz7">{s.title}</span>
              </li>)}
            <li className="flex items-center gap-2 text-[15px]" data-id="element-1063" data-mp="1a3e7o3n519i2">
              {creditAuthorized ? <CheckCircle2Icon className="h-4 w-4 text-positive-500" aria-label="Complete" data-id="element-1064" data-mp="1a5gyur0118od" /> : <AlertCircleIcon className="h-4 w-4 text-critical-500" aria-label="Needs attention" data-id="element-1065" data-mp="ve7ybmul62wg" />}
              {creditAuthorized ? <span className="text-ink-700" data-id="element-1066" data-mp="me1oqc20dn2k">Credit authorized</span> : <Link to="/credit" className="text-critical-700 underline" data-id="element-1067" data-mp="qfeuyn90jsa0">
                  Authorize credit
                </Link>}
            </li>
          </ul>
        </section>

        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl bg-white p-5 ring-1 ring-ink-200" data-id="element-1068" data-mp="hep31umvim1k">
          <input type="checkbox" checked={certified} onChange={e => setCertified(e.target.checked)} className="mt-0.5 h-5 w-5 rounded accent-pulse-600" data-id="element-1069" data-mp="26twoso71tk20" />
          <span data-id="element-1070" data-mp="2bryehryzchmg">
            <span className="block text-[15px] text-ink-900" data-id="element-1071" data-mp="2bqdkq4khp2pn">I confirm the information in my application is true and complete to the best of my knowledge.</span>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-caution-50 px-2.5 py-1 text-[11px] font-medium text-caution-700" data-id="element-1072" data-mp="25bokt6tj7v62">
              <FileWarningIcon className="h-3 w-3" aria-hidden data-id="element-1073" data-mp="1vebjf8g3ciaw" /> Placeholder copy · pending compliance review
            </span>
          </span>
        </label>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1074" data-mp="2t1ir15jcilhc">
          <p className="text-sm text-ink-500" data-id="element-1075" data-mp="2o3h9ip1kiu7z">{issues ? `${issues} item${issues > 1 ? 's' : ''} in your application still need attention.` : 'After you submit, your document list is ready right away.'}</p>
          <Button size="lg" disabled={!ready} loading={submitting} icon={<SendIcon className="h-4 w-4" data-id="element-1077" data-mp="3bstltxs6ziei" />} onClick={submit} data-id="element-1076" data-mp="2ecw5gm34kxho">
            {submitting ? 'Submitting…' : 'Submit application'}
          </Button>
        </div>
      </main>
      <ComplianceFooter className="max-w-[640px]" data-id="element-1078" data-mp="3fahbblpqy2jv" />
    </div>;
}